'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useState } from 'react'

import IconCode from '@/components/assets/icon_code.svg'
import IconFB from '@/components/assets/icon_fb.svg'
import IconLink from '@/components/assets/icon_link.svg'
import IconShare from '@/components/assets/icon_share.svg'
import IconTwitter from '@/components/assets/icon_twitter.svg'
import EmbedDialog from '@/components/EmbedDialog'
import PopoverMenu from '@/components/PopoverMenu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import ViverseButton from '@/components/ViverseButton'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { useEnableViewMode } from '@/hooks/useEnableViewMode'
import { useShareLink } from '@/hooks/useShareLink'
import { buildShareUrl } from '@/lib/share-link'
import { cn } from '@/lib/utils'

type SharePopoverButtonProps = {
  className?: string
  iconClassName?: string
}

function SharePopoverButton({ className, iconClassName }: SharePopoverButtonProps) {
  const [open, setOpen] = useState(false)
  const [isEmbedDialogOpen, setIsEmbedDialogOpen] = useState(false)

  const t = useTranslations()
  const { roomData } = useCheckEnterRoomContext()
  const { standaloneUrl } = useWorldDetailsContext()
  const { is3DCommerce } = useWorldDetailsContext()
  const enableViewMode = useEnableViewMode()
  const shareUrl = buildShareUrl(roomData.hub_sid, {
    view: is3DCommerce || enableViewMode,
  })

  const { copyLink, createSharePlatformLink } = useShareLink(shareUrl)

  const handleCopyLink = () => {
    copyLink()
    setOpen(false)
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <ViverseButton
            className={cn('flex min-w-fit gap-2', className)}
            variant="filter"
            onClick={() => setOpen(true)}
          >
            <IconShare className={iconClassName} />
            {/* <span className="subheader-3 text-vive-primary-30 max-md:hidden">
              {t('action-list.share')}
            </span> */}
          </ViverseButton>
        </PopoverTrigger>
        <PopoverContent side="bottom" className="mt-2 border-none bg-transparent p-0">
          <PopoverMenu className="bg-vive-primary-70 rounded-none px-0">
            <PopoverMenu.Group>
              <Link href={createSharePlatformLink('twitter', roomData.title)} target="_blank">
                <PopoverMenu.Item className="rounded-none">
                  <IconTwitter className="fill-vive-primary-20 size-4" />
                  <div className="body-1 text-inherit">{t('profile-page.share-twitter')}</div>
                </PopoverMenu.Item>
              </Link>
              <Link href={createSharePlatformLink('facebook')} target="_blank">
                <PopoverMenu.Item className="rounded-none">
                  <IconFB className="fill-vive-primary-20 size-4" />
                  <div className="body-1 text-inherit">{t('profile-page.share-facebook')}</div>
                </PopoverMenu.Item>
              </Link>
              <PopoverMenu.Item className="rounded-none" onClick={handleCopyLink}>
                <IconLink className="fill-vive-primary-20 size-4" />
                <div className="body-1 text-inherit">{t('profile-page.share-copy-link')}</div>
              </PopoverMenu.Item>
              {!standaloneUrl && (
                <PopoverMenu.Item
                  className="group rounded-none"
                  onClick={() => setIsEmbedDialogOpen(true)}
                >
                  <IconCode className="fill-vive-primary-20 size-4" />
                  <div className="body-1 group-hover:text text-inherit">
                    {t('embed-modal.title')}
                  </div>
                </PopoverMenu.Item>
              )}
            </PopoverMenu.Group>
          </PopoverMenu>
        </PopoverContent>
      </Popover>
      <EmbedDialog
        title={t('embed-modal.title')}
        info={roomData}
        isDialogOpen={isEmbedDialogOpen}
        is3DCommerce={is3DCommerce}
        handleOpenChange={setIsEmbedDialogOpen}
        handleClose={() => setIsEmbedDialogOpen(false)}
      />
    </>
  )
}

export default SharePopoverButton
